// frontend/src/pages/SignupPage.tsx
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { requestOtp, verifyOtp, ApiError, googleAuth, GoogleAuthNeedsUsername, appleAuth, AppleAuthNeedsUsername } from '@/api/auth';
import { useAuth } from '@/contexts/AuthContext';
import { mixpanelTrack } from '@/services/mixpanel';
import GoogleAuthButton from '@/components/auth/GoogleAuthButton';
import { signInWithApple } from '@/services/appleSignIn';
import SEO from '@/components/common/SEO';
import { getActiveReferral, clearReferral } from '@/utils/referral';
import { readAuthIntent, withAuthIntent, CREATE_LEAGUE_RESUME_PATH } from '@/utils/authIntent';

const normalizePhoneNumberFE = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return cleaned;
  if (cleaned.length === 11 && cleaned.startsWith('1')) return `+${cleaned}`;
  if (cleaned.length === 10) return `+1${cleaned}`;
  return phone;
};

// Mirrors the backend's USERNAME_REGEX (^[a-zA-Z0-9_]{3,30}$) and its
// _make_username_suggestion() helper, which turns spaces into underscores.
// Sanitizing as the user types matters: the server only reports the rule
// AFTER a failed round-trip, so an untyped-but-invalid character used to cost
// a full request to discover. A real signup burned five attempts over 64
// seconds typing a club name with spaces before giving up on them (2026-08-08).
const USERNAME_PATTERN = '[a-zA-Z0-9_]{3,30}';
const USERNAME_HINT = 'Letters, numbers and underscores only (3-30 characters).';

const sanitizeUsername = (raw: string): string =>
  raw.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '').slice(0, 30);

const SignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading, completeLogin } = useAuth();
  // Allowlisted funnel marker (utils/authIntent). Read per render so it stays
  // current, threaded onto the login toggle link, and stamped on the funnel
  // events below. completeLogin does its own read of the URL for routing.
  const authIntent = readAuthIntent(location.search);
  const intentProps = authIntent ? { 'Intent': authIntent } : {};
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Unified phone verification. The account is created only after a valid
  // code; existing numbers use the identical request/verify path. Verifying
  // hands the token to completeLogin, which runs the post-login action handler —
  // including the anonymous-funnel DFS lineup auto-submit. Bouncing to /login
  // instead (the old flow) silently dropped that: the stashed lineup sat
  // unsubmitted until the user completed a second full OTP round trip.
  const [phoneStep, setPhoneStep] = useState<'form' | 'code_sent'>('form');
  const [code, setCode] = useState('');
  const [isLoadingVerify, setIsLoadingVerify] = useState(false);
  const [isLoadingResend, setIsLoadingResend] = useState(false);

  // Google auth state
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [googleNeedsUsername, setGoogleNeedsUsername] = useState<GoogleAuthNeedsUsername | null>(null);
  const [googleIdToken, setGoogleIdToken] = useState<string>('');
  const [googleUsername, setGoogleUsername] = useState('');

  // Apple auth state
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [appleNeedsUsername, setAppleNeedsUsername] = useState<AppleAuthNeedsUsername | null>(null);
  const [appleIdToken, setAppleIdToken] = useState<string>('');
  const [appleAuthCode, setAppleAuthCode] = useState<string>('');
  const [appleUsername, setAppleUsername] = useState('');

  // Own only the initial-session redirect. A signup completed on this page is
  // routed by completeLogin, so this effect must not race its intent handling.
  const shouldRedirectExistingSession = useRef(isAuthenticated || isAuthLoading);
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && shouldRedirectExistingSession.current) {
      navigate(authIntent === 'create_league' ? CREATE_LEAGUE_RESUME_PATH : '/home', { replace: true });
    }
    if (!isAuthLoading) shouldRedirectExistingSession.current = false;
  }, [authIntent, isAuthLoading, isAuthenticated, navigate]);

  // Funnel visibility (CREATE-LEAGUE-FUNNEL.md §3.6): only intent-carrying
  // visits are gates; a plain /signup visit is ordinary traffic.
  useEffect(() => {
    if (authIntent) mixpanelTrack('Auth Gate Shown', { 'Intent': authIntent, 'Gate': 'signup' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleSuccess = async (credential: string) => {
    setError(null);
    setIsLoadingGoogle(true);
    // Google's rendered web button exposes no click hook, so credential
    // receipt is the earliest "started" moment available for this method.
    mixpanelTrack('Auth Method Started', { 'Method': 'google', ...intentProps });
    try {
      const response = await googleAuth(credential);
      if ('needs_username' in response) {
        setGoogleIdToken(credential);
        setGoogleNeedsUsername(response);
        setGoogleUsername(response.suggested_username);
      } else {
        await completeLogin(response.access_token);
        if (response.is_new_user) {
          mixpanelTrack('Signed Up', { 'Signup Method': 'Google', ...intentProps });
        } else {
          mixpanelTrack('Logged In', { 'Login Method': 'Google', ...intentProps });
        }
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : 'Google sign-up failed. Please try again.');
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleGoogleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoadingGoogle(true);
    try {
      const response = await googleAuth(googleIdToken, googleUsername.trim());
      if ('needs_username' in response) {
        setError('That username is taken. Please choose another.');
      } else {
        await completeLogin(response.access_token);
        mixpanelTrack('Signed Up', { 'Signup Method': 'Google', ...intentProps });
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : 'Failed to create account. Please try again.');
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handleAppleClick = async () => {
    setError(null);
    setIsLoadingApple(true);
    mixpanelTrack('Auth Method Started', { 'Method': 'apple', ...intentProps });
    try {
      const { idToken, code } = await signInWithApple();
      const response = await appleAuth(idToken, code);
      if ('needs_username' in response) {
        setAppleIdToken(idToken);
        setAppleAuthCode(code);
        setAppleNeedsUsername(response);
        setAppleUsername('');
      } else {
        await completeLogin(response.access_token);
        if (response.is_new_user) {
          mixpanelTrack('Signed Up', { 'Signup Method': 'Apple', ...intentProps });
        } else {
          mixpanelTrack('Logged In', { 'Login Method': 'Apple', ...intentProps });
        }
      }
    } catch (err: any) {
      // Apple's JS SDK rejects with { error: 'popup_closed_by_user' | 'user_cancelled_authorize' | ... }
      // when the user dismisses the popup. Dismissal stays a silent no-op in
      // the UI but is tracked: a dismissed sheet and a never-tapped button were
      // indistinguishable in the 2026-08-10 funnel autopsy.
      const code = err?.error || err?.message || '';
      if (code.includes('popup_closed_by_user') || code.includes('user_cancelled_authorize')) {
        mixpanelTrack('Auth Method Dismissed', { 'Method': 'apple', ...intentProps });
      } else {
        setError(err instanceof ApiError ? err.message : 'Apple sign-up failed. Please try again.');
      }
    } finally {
      setIsLoadingApple(false);
    }
  };

  const handleAppleUsernameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoadingApple(true);
    try {
      const response = await appleAuth(appleIdToken, appleAuthCode, appleUsername.trim());
      if ('needs_username' in response) {
        setError('That username is taken. Please choose another.');
      } else {
        await completeLogin(response.access_token);
        mixpanelTrack('Signed Up', { 'Signup Method': 'Apple', ...intentProps });
      }
    } catch (err: any) {
      setError(err instanceof ApiError ? err.message : 'Failed to create account. Please try again.');
    } finally {
      setIsLoadingApple(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setIsRateLimited(false);
    setSuccessMessage(null);

    const normalizedPhone = normalizePhoneNumberFE(phone);
    if (!normalizedPhone.startsWith('+1') || normalizedPhone.length < 12) {
      setError('Please enter a valid US phone number (e.g., 2025550143).');
      setIsLoading(false);
      return;
    }
    mixpanelTrack('Auth Method Started', { 'Method': 'otp', ...intentProps });
    mixpanelTrack('Signup Attempt', { 'Signup Method': 'OTP', ...intentProps });

    try {
      await requestOtp(normalizedPhone);
      mixpanelTrack('OTP Requested', { 'Success': true, 'Context': 'Signup' });
      setPhoneStep('code_sent');
    } catch (err: any) {
      let errorMessage = 'Could not send a verification code. Please try again.';
      const isRL = err instanceof ApiError && err.status === 429;
      if (err instanceof ApiError) errorMessage = err.message;
      else if (err instanceof Error) errorMessage = err.message;
      setIsRateLimited(isRL);
      setError(errorMessage);
      mixpanelTrack('Signup Failed', { 'Signup Method': 'OTP', 'Error': errorMessage, 'Rate Limited': isRL });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsRateLimited(false);
    setSuccessMessage(null);
    setIsLoadingVerify(true);
    const normalizedPhone = normalizePhoneNumberFE(phone);
    const referralSource = getActiveReferral();
    try {
      const loginResponse = await verifyOtp(normalizedPhone, code, referralSource);
      // completeLogin runs the post-login action handler (pending DFS lineup
      // auto-submit + GAME ON flag + scorecard navigation) — same as the
      // Google/Apple signup paths.
      await completeLogin(loginResponse.access_token);
      if (loginResponse.is_new_user) {
        if (referralSource) clearReferral();
        mixpanelTrack('Signed Up', {
          'Signup Method': 'Phone',
          'Referral Source': referralSource || null,
          ...intentProps,
        });
      } else {
        mixpanelTrack('Logged In', { 'Login Method': 'OTP', 'Context': 'Signup Page', ...intentProps });
      }
    } catch (err: any) {
      const isRL = err instanceof ApiError && err.status === 429;
      setIsRateLimited(isRL);
      setError(err instanceof ApiError ? err.message : 'Verification failed. Please try again.');
      mixpanelTrack('Login Failed', { 'Login Method': 'OTP', 'Context': 'Post-Signup', 'Error': err?.message, 'Rate Limited': isRL });
    } finally {
      setIsLoadingVerify(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setIsRateLimited(false);
    setSuccessMessage(null);
    setIsLoadingResend(true);
    try {
      await requestOtp(normalizePhoneNumberFE(phone));
      setSuccessMessage('New code sent.');
      mixpanelTrack('OTP Requested', { 'Success': true, 'Context': 'Post-Signup Resend' });
    } catch (err: any) {
      const isRL = err instanceof ApiError && err.status === 429;
      setIsRateLimited(isRL);
      setError(err instanceof ApiError ? err.message : 'Failed to send verification code.');
      mixpanelTrack('OTP Request Failed', { 'Error': err?.message, 'Rate Limited': isRL, 'Context': 'Post-Signup Resend' });
    } finally {
      setIsLoadingResend(false);
    }
  };

  const errorBanner = error && (
    <p className={`mb-4 text-sm text-center rounded p-2 ${isRateLimited ? 'text-amber-400 bg-amber-900/40 border border-amber-500/60' : 'text-secondary bg-red-900/50 border border-secondary'}`}>
      {error}
    </p>
  );

  // --- Post-signup phone verification step ---
  if (phoneStep === 'code_sent') {
    return (
      <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
        <div className="bg-surface-card p-8 rounded-lg shadow-xl w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Fantasy Futbol" className="w-28 h-28 object-contain" />
          </div>
          <h2 className="text-xl font-heading font-bold text-accent-light-blue mb-2 text-center">Enter your code</h2>
          <p className="text-sm text-gray-400 text-center mb-6">
            We sent a code to {normalizePhoneNumberFE(phone)}. New here? Verifying creates your account.
          </p>
          {errorBanner}
          {successMessage && (
            <p className="mb-4 text-sm text-center text-green-400 bg-green-900/50 border border-green-400 rounded p-2">
              {successMessage}
            </p>
          )}
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={6}
              placeholder="6-digit code"
              disabled={isLoadingVerify}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50 text-center text-xl tracking-widest"
            />
            <button
              type="submit"
              disabled={isLoadingVerify}
              className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 ${isLoadingVerify ? 'bg-accent-yellow text-primary animate-pulse cursor-not-allowed' : 'bg-secondary hover:bg-red-700 text-white'}`}
            >
              {isLoadingVerify ? 'Verifying...' : 'VERIFY & CONTINUE'}
            </button>
          </form>
          <div className="flex flex-col items-center gap-2 mt-4">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoadingResend || isLoadingVerify}
              className="text-accent-light-blue hover:text-accent-yellow underline text-xs disabled:opacity-50"
            >
              {isLoadingResend ? 'Sending...' : 'Resend code'}
            </button>
            <button
              type="button"
              onClick={() => { setPhoneStep('form'); setCode(''); setError(null); setSuccessMessage(null); }}
              className="text-xs text-gray-500 hover:text-gray-300 underline"
            >
              Change phone number
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Apple username selection step ---
  if (appleNeedsUsername) {
    return (
      <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
        <div className="bg-surface-card p-8 rounded-lg shadow-xl w-full max-w-sm">
          <h2 className="text-2xl font-heading font-bold text-accent-light-blue mb-2 text-center">Create a Username</h2>
          <p className="text-sm text-gray-400 text-center mb-6">Pick a username to complete your account</p>
          {errorBanner}
          <form onSubmit={handleAppleUsernameSubmit} className="space-y-3">
            <input
              type="text"
              value={appleUsername}
              onChange={(e) => setAppleUsername(sanitizeUsername(e.target.value))}
              required
              minLength={3}
              maxLength={30}
              pattern={USERNAME_PATTERN}
              title={USERNAME_HINT}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              aria-describedby="apple-username-hint"
              placeholder="Choose a username"
              disabled={isLoadingApple}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50"
            />
            <p id="apple-username-hint" className="text-xs text-gray-400">{USERNAME_HINT}</p>
            <button
              type="submit"
              disabled={isLoadingApple}
              className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 ${isLoadingApple ? 'bg-gray-500 text-gray-300 animate-pulse cursor-not-allowed' : 'bg-accent-light-blue hover:bg-blue-400 text-primary'}`}
            >
              {isLoadingApple ? 'Creating Account...' : 'Continue'}
            </button>
            <button type="button" onClick={() => { setAppleNeedsUsername(null); setError(null); }} className="w-full text-xs text-gray-500 hover:text-gray-300 text-center py-1">
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Google username selection step ---
  if (googleNeedsUsername) {
    return (
      <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
        <div className="bg-surface-card p-8 rounded-lg shadow-xl w-full max-w-sm">
          <div className="flex justify-center mb-6">
            <img src="/logo.png" alt="Fantasy Futbol" className="w-28 h-28 object-contain" />
          </div>
          <h2 className="text-xl font-heading font-bold text-accent-light-blue mb-2 text-center">One last step</h2>
          <p className="text-sm text-gray-400 text-center mb-6">Choose a username to complete your account</p>
          {errorBanner}
          <form onSubmit={handleGoogleUsernameSubmit} className="space-y-3">
            <input
              type="text"
              value={googleUsername}
              onChange={(e) => setGoogleUsername(sanitizeUsername(e.target.value))}
              required
              minLength={3}
              maxLength={30}
              pattern={USERNAME_PATTERN}
              title={USERNAME_HINT}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoComplete="username"
              aria-describedby="google-username-hint"
              placeholder="Choose a username"
              disabled={isLoadingGoogle}
              className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-50"
            />
            <p id="google-username-hint" className="text-xs text-gray-400">{USERNAME_HINT}</p>
            <button
              type="submit"
              disabled={isLoadingGoogle}
              className={`w-full py-3 px-4 rounded-lg font-heading font-bold text-lg transition-colors duration-200 ${isLoadingGoogle ? 'bg-gray-500 text-gray-300 animate-pulse cursor-not-allowed' : 'bg-accent-light-blue hover:bg-blue-400 text-primary'}`}
            >
              {isLoadingGoogle ? 'Creating Account...' : 'Continue'}
            </button>
            <button type="button" onClick={() => { setGoogleNeedsUsername(null); setError(null); }} className="w-full text-xs text-gray-500 hover:text-gray-300 text-center py-1">
              Cancel
            </button>
          </form>
        </div>
      </div>
    );
  }

  // --- Main signup screen ---
  return (
    <div className="flex justify-center bg-primary text-gray-200 font-body p-4 pt-6 sm:pt-10">
      <SEO
        title="Sign Up | Fantasy Futbol"
        description="Create a Fantasy Futbol account to play daily and season-long fantasy soccer for the Premier League and La Liga with fantasy-football-style scoring."
        path="/signup"
        noindex
      />
      <div className="w-full max-w-sm space-y-6">

        {/* Logo + tagline */}
        <div className="flex flex-col items-center gap-3">
          <img src="/logo.png" alt="Fantasy Futbol" className="w-36 h-36 object-contain" />
          <p className="text-yellow-500 font-bold text-sm text-center">Fantasy Football for Soccer</p>
        </div>

        <div className="bg-surface-card p-6 rounded-lg shadow-xl space-y-4">
          {errorBanner}
          {successMessage && (
            <p className="mb-4 text-sm text-center text-green-400 bg-green-900/50 border border-green-400 rounded p-2">
              {successMessage}
            </p>
          )}

          {/* Google — primary CTA */}
          <GoogleAuthButton
            mode="signup"
            isLoading={isLoadingGoogle}
            onSuccess={handleGoogleSuccess}
            onError={(msg) => setError(msg)}
          />

          {/* Apple */}
          <button
            type="button"
            onClick={handleAppleClick}
            disabled={isLoadingApple}
            aria-label="Sign up with Apple"
            className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium text-base bg-black text-white hover:bg-gray-900 transition-colors ${isLoadingApple ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
              <path d="M17.05 13.04c-.03-2.95 2.4-4.37 2.51-4.44-1.37-2-3.5-2.28-4.26-2.31-1.81-.18-3.54 1.07-4.46 1.07-.93 0-2.35-1.05-3.86-1.02-1.99.03-3.82 1.15-4.84 2.93-2.06 3.57-.53 8.86 1.49 11.76.98 1.42 2.15 3.02 3.69 2.96 1.48-.06 2.04-.96 3.83-.96 1.78 0 2.29.96 3.86.93 1.59-.03 2.6-1.45 3.58-2.88 1.13-1.65 1.6-3.26 1.62-3.34-.03-.01-3.1-1.19-3.16-4.7zM14.46 4.5c.81-.99 1.36-2.36 1.21-3.73-1.17.05-2.59.78-3.43 1.76-.75.87-1.41 2.27-1.24 3.6 1.3.1 2.65-.66 3.46-1.63z" />
            </svg>
            <span>{isLoadingApple ? 'Loading...' : 'Sign up with Apple'}</span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-gray-700" />
            <span className="text-xs text-gray-500">or sign up with phone</span>
            <div className="flex-1 border-t border-gray-700" />
          </div>

          {/* Phone signup form */}
          <form onSubmit={handleSignup} className="space-y-3">
            <div>
              <label htmlFor="phone-signup" className="block text-xs font-medium text-gray-400 mb-1">Phone Number</label>
              <input
                type="tel"
                inputMode="tel"
                id="phone-signup"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                placeholder="2025550143"
                disabled={isLoading}
                className="w-full p-3 rounded-lg bg-primary border border-gray-600 focus:outline-none focus:border-accent-yellow text-gray-200 placeholder-gray-500 disabled:opacity-70"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-2.5 px-4 rounded-lg font-heading font-bold text-base transition-colors duration-200 border ${
                isLoading ? 'border-gray-600 text-gray-500 animate-pulse cursor-not-allowed'
                : 'border-gray-500 text-gray-300 hover:border-accent-light-blue hover:text-accent-light-blue'
              }`}
            >
              {isLoading ? 'Sending Code...' : 'Continue with Phone'}
            </button>
          </form>

          {/* Login link - carries the auth intent so a create_league arrival
              keeps its post-auth destination across the toggle */}
          <p className="text-center text-xs text-gray-500 pt-1">
            Already have an account?{' '}
            <Link to={withAuthIntent('/login', authIntent)} className="text-accent-light-blue hover:text-accent-yellow underline">
              Log in
            </Link>
          </p>
        </div>

      </div>
    </div>
  );
};

export default SignupPage;
